import { supabase } from '@/integrations/supabase/client';
import { openAuthGate } from '@/context/AuthGateContext';
import type { PricingStrategyKind } from '@/types/pricing';

export type AiProvider = 'openai' | 'anthropic' | 'groq';
export type AiOperation =
  | 'identify'
  | 'generate'
  | 'keywords'
  | 'nlq'
  | 'comps'
  | 'appraise'
  | 'reply'
  | 'explain-valuation';

export interface IdentifyItemPayload {
  imageUrl?: string;
  imageBase64?: string;
  mimeType?: string;
  /** Free-text hint (e.g. typed item name) the server folds into the prompt. */
  nameHint?: string;
}

export interface IdentifyItemResult {
  name: string;
  category: string;
  era: string;
  materials: string[];
  condition: string;
  estimatedValue: string;
  highlights: string[];
  /**
   * fl-404: best-effort PACKED shipping estimate from the built-in prompt
   * (absent under a custom prompt). Untrusted model numbers — the intake
   * form range-checks them and the user confirms before save.
   */
  estimatedWeightOz?: number;
  estimatedDims?: { lengthIn?: number; widthIn?: number; heightIn?: number };
  /**
   * fl-rnu Phase 2 structured identify (Task D2): a best-effort structured
   * valuation prefill, loosely `{category, fields}` (or `null` for a
   * non-collectible item). Emitted ONLY by the built-in identify-item prompt
   * — a custom user prompt omits the key entirely. This is raw, untrusted
   * model output: it must ALWAYS pass through `sanitizeModelIdentity`
   * (src/lib/valuation/mapping.ts) before it touches state or the DB. Never
   * read or store this field directly.
   */
  valuationIdentity?: unknown;
}

export interface GenerateListingPayload {
  name: string;
  category: string;
  condition: string;
  description?: string;
  era?: string;
  materials?: string[];
  platform?: string;
}

export interface GenerateListingResult {
  title: string;
  description: string;
  features: string[];
  conditionStatement: string;
}

export interface ExtractKeywordsPayload {
  text: string;
}

export interface NaturalLanguageQueryPayload {
  query: string;
}

export interface AppraisePayload {
  imageBase64s: string[];
  mimeTypes: string[];
  condition: string;
  era: string;
  materials: string;
}

export interface AppraiseResult {
  estimated_value: { low: number; high: number };
  key_value_drivers: string[];
  suggested_listing_strategy: string;
  item_name: string;
  category: string;
}

export interface NaturalLanguageQueryResult {
  summary: string;
  items?: Array<{ label: string; value: string }>;
  actions?: Array<{ label: string; action: string }>;
}

export interface SuggestReplyPayload {
  platform: string;
  buyerMessage: string;
  itemContext?: string;
}

export interface SuggestReplyResult {
  reply: string;
}

export interface SearchCompsPayload {
  query?: string;
  imageBase64?: string;
  mimeType?: string;
  platform?: string;
}

export interface SoldComp {
  title: string;
  platform: string;
  soldPrice: number;
  soldDate: string;
  condition: string;
  source?: string;
  itemUrl?: string;
}

export interface SourceInfo {
  source: string;
  weight: number;
  comps: number;
}

export interface SearchCompsResult {
  comps: SoldComp[];
  stats: {
    median: number;
    p25: number;
    p75: number;
    count: number;
  };
  suggestedListPrice: number;
  query: string;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  sourceBreakdown?: SourceInfo[];
  /**
   * Provenance. `ai-estimate` = the LLM `search-comps` op guessed these numbers
   * (no observed sold data). Absent/other = built from real comp sources.
   * UIs must never present `ai-estimate` results as "recent comps".
   */
  source?: 'ai-estimate' | 'aggregator' | 'ebay-browse';
  /** `active-listings` = eBay asking prices, not sales (ebay-proxy
   *  search-comps, fl-kw3). */
  basis?: 'active-listings';
}

/** True when a comps result is an LLM guess rather than observed sales. */
export function isAiEstimate(
  result: Pick<SearchCompsResult, 'source'> | null | undefined,
): boolean {
  return result?.source === 'ai-estimate';
}

/** True when a comps result is current eBay asking prices (active listings)
 *  rather than sales. UIs must not call these "sold comps" (fl-kw3). */
export function isAskingPrices(
  result: Pick<SearchCompsResult, 'source' | 'basis'> | null | undefined,
): boolean {
  return result?.basis === 'active-listings' || result?.source === 'ebay-browse';
}

/**
 * AI valuation explanation. The Valuation page sends the current
 * slider-driven price context (fair value, suggested price + range, condition,
 * sale speed, sample size, and a handful of real sold comps) and the
 * `explain-valuation` edge operation returns a short plain-English rationale,
 * the top price drivers, and a confidence level. FIXED CONTRACT — the edge side
 * relies on these exact field names/shapes; keep them in lockstep.
 */
export interface ExplainValuationPayload {
  itemName: string;
  category: string;
  fairValue: number;
  suggestedPrice: number;
  rangeLow: number;
  rangeHigh: number;
  condition: string;
  sellSpeedLabel: string;
  daysToSell: number;
  sampleSize: number;
  comps: Array<{ soldPrice: number; condition: string; soldDate: string; platform: string }>;
  marketTrend?: string;
}

export interface ExplainValuationResult {
  explanation: string;
  drivers: string[];
  confidence_level: 'HIGH' | 'MEDIUM' | 'LOW';
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const AI_PROXY_URL = `${SUPABASE_URL}/functions/v1/ai-proxy`;

async function getAuthHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    openAuthGate('use AI features');
    throw new Error('unauthenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

/** Low-level proxy call — used internally by convenience methods and by tests. */
export async function callAI(req: {
  provider: AiProvider;
  operation: AiOperation | string;
  payload: Record<string, unknown>;
}): Promise<unknown> {
  const headers = await getAuthHeaders();
  const resp = await fetch(AI_PROXY_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      provider: req.provider,
      operation: req.operation,
      payload: req.payload,
    }),
    signal: AbortSignal.timeout(30_000),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ error: { message: resp.statusText } }));
    throw new ProxyHttpError(
      body?.error?.code,
      body?.error?.message ?? `AI proxy ${resp.status}`,
      resp.status,
    );
  }
  return resp.json();
}

/** Structured error that carries the upstream error code and HTTP status. */
export class ProxyHttpError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ProxyHttpError';
    this.code = code;
    this.status = status;
  }
}

async function invokeProxy<T>(
  provider: AiProvider,
  operation: AiOperation | string,
  payload: unknown,
): Promise<T> {
  const data = await callAI({ provider, operation, payload: payload as Record<string, unknown> });
  const result = (data as { result: string }).result;
  try {
    return JSON.parse(result) as T;
  } catch {
    return result as unknown as T;
  }
}

export interface StreamUsage {
  tokens: number;
  cost_usd: number;
}

/** Streamable AI operations — server enforces this list. */
export type StreamableOperation = 'nlq' | 'reply';

/**
 * Stream an AI response from ai-proxy via SSE. Invokes onDelta for each
 * text chunk, onDone when the stream ends cleanly with usage info, and
 * onError for any failure. Honors AbortSignal for client-driven cancel.
 *
 * Only `nlq` and `reply` operations support streaming on the server.
 */
export async function streamAI(
  operation: StreamableOperation,
  payload: unknown,
  onDelta: (text: string) => void,
  onDone: (usage: StreamUsage) => void,
  onError: (err: Error) => void,
  signal?: AbortSignal,
): Promise<void> {
  let headers: HeadersInit;
  try {
    headers = await getAuthHeaders();
  } catch (err) {
    onError(err instanceof Error ? err : new Error('auth failed'));
    return;
  }

  let resp: Response;
  try {
    resp = await fetch(AI_PROXY_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        provider: 'groq',
        operation,
        payload,
        stream: true,
      }),
      signal,
    });
  } catch (err) {
    // AbortError is a normal cancellation; do not surface as a hard error
    if (err instanceof Error && err.name === 'AbortError') return;
    onError(err instanceof Error ? err : new Error('network error'));
    return;
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({ error: { message: resp.statusText } }));
    onError(
      new ProxyHttpError(
        body?.error?.code,
        body?.error?.message ?? `AI proxy ${resp.status}`,
        resp.status,
      ),
    );
    return;
  }
  if (!resp.body) {
    onError(new Error('AI proxy returned empty body'));
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let receivedDone = false;

  const onAbort = () => {
    reader.cancel().catch(() => {
      /* ignore */
    });
  };
  signal?.addEventListener('abort', onAbort);

  try {
    while (true) {
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (err) {
        if (signal?.aborted) return;
        throw err;
      }
      const { done, value } = chunk;
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith('data: ')) continue;
        let parsed: {
          type?: string;
          text?: string;
          message?: string;
          usage?: StreamUsage;
          done?: boolean;
          error?: string;
        };
        try {
          parsed = JSON.parse(line.slice(6));
        } catch {
          continue;
        }

        // New envelope: {type:'delta'|'done'|'error'}
        if (parsed.type === 'delta' && typeof parsed.text === 'string') {
          onDelta(parsed.text);
          continue;
        }
        if (parsed.type === 'done') {
          receivedDone = true;
          onDone(parsed.usage ?? { tokens: 0, cost_usd: 0 });
          return;
        }
        if (parsed.type === 'error') {
          onError(new Error(parsed.message ?? 'stream error'));
          return;
        }

        // Legacy envelope back-compat: {text}, {done:true}, {error}
        if (typeof parsed.text === 'string') {
          onDelta(parsed.text);
          continue;
        }
        if (parsed.done) {
          receivedDone = true;
          onDone({ tokens: 0, cost_usd: 0 });
          return;
        }
        if (parsed.error) {
          onError(new Error(parsed.error));
          return;
        }
      }
    }
    if (!receivedDone && !signal?.aborted) {
      // Stream closed without a `done` event — treat as a clean end.
      onDone({ tokens: 0, cost_usd: 0 });
    }
  } catch (err) {
    if (signal?.aborted) return;
    onError(err instanceof Error ? err : new Error('stream read error'));
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}

/**
 * Stream an NLQ response from ai-proxy as text chunks via ReadableStream.
 * Wrapper around streamAI() that preserves the legacy ReadableStream<string>
 * surface used by older callers and tests.
 */
export function nlqStream(query: string, item?: Record<string, unknown>): ReadableStream<string> {
  return new ReadableStream({
    async start(controller) {
      const ac = new AbortController();
      await streamAI(
        'nlq',
        { query, item },
        (text) => controller.enqueue(text),
        () => controller.close(),
        (err) => controller.error(err),
        ac.signal,
      );
    },
  });
}

/** Logger that consumes an NLQ stream and returns the full text. */
export async function collectStream(stream: ReadableStream<string>): Promise<string> {
  const reader = stream.getReader();
  const parts: string[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
  }
  return parts.join('');
}

/**
 * Sprint 18 — Whisper transcription via ai-proxy.transcribe.
 *
 * Doesn't go through `invokeProxy` because the transcribe response is
 * `{ ok, transcript, durationSec }` (shaped for the voice-memo UX),
 * not the `{ result: <stringified-JSON> }` envelope the other ops use.
 */
export interface TranscribeResult {
  transcript: string;
  durationSec?: number;
}

export interface PhotoScoreResult {
  overallScore: number; // 0-100
  flags: string[]; // e.g. "too dark", "cluttered background", "item cropped"
}

/** Soft cap matches the edge function's hard cap. We surface a clear
 *  client-side error rather than spending the round trip just to be
 *  rejected. 5 MB base64 ≈ 3.75 MB binary which is well above a
 *  60s 192kbps mono recording (~1.5 MB). */
const MAX_AUDIO_BASE64_LEN = 5 * 1024 * 1024;

async function blobToBase64NoPrefix(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Strip the "data:audio/webm;base64," prefix to get pure base64.
      const idx = dataUrl.indexOf(',');
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

export async function transcribeAudio(
  blob: Blob,
  opts?: { language?: string },
): Promise<TranscribeResult> {
  if (!blob || blob.size === 0) {
    throw new Error('Audio is empty');
  }
  const audio_base64 = await blobToBase64NoPrefix(blob);
  if (audio_base64.length === 0) {
    throw new Error('Audio is empty');
  }
  if (audio_base64.length > MAX_AUDIO_BASE64_LEN) {
    throw new Error('Audio exceeds the 5 MB limit');
  }

  const data = await callAI({
    provider: 'openai',
    operation: 'transcribe',
    payload: {
      audio_base64,
      mime_type: blob.type || 'audio/webm',
      ...(opts?.language ? { language: opts.language } : {}),
    },
  });
  const resp = data as { ok?: boolean; transcript?: string; durationSec?: number };
  if (!resp || resp.ok !== true || typeof resp.transcript !== 'string') {
    throw new Error('Transcription failed');
  }
  return { transcript: resp.transcript, durationSec: resp.durationSec };
}

export const ai = {
  async identifyItem(payload: IdentifyItemPayload, provider: AiProvider = 'groq') {
    const result = await invokeProxy<IdentifyItemResult>(provider, 'identify', payload);
    // The model can return valid-but-wrong-shaped JSON (or invokeProxy can
    // fall back to a raw string). Surfacing that as an error beats silently
    // rendering an all-blank identification card (fl-9xr).
    if (
      !result ||
      typeof result !== 'object' ||
      (typeof (result as IdentifyItemResult).name !== 'string' &&
        typeof (result as IdentifyItemResult).category !== 'string')
    ) {
      throw new Error('AI returned an unexpected response shape — please try again');
    }
    return result;
  },

  generateListing(payload: GenerateListingPayload, provider: AiProvider = 'groq') {
    return invokeProxy<GenerateListingResult>(provider, 'generate', payload);
  },

  /**
   * Conversational price refinement. The user discusses item specifics and
   * the agent refines the recommended price, grounded in the sold-comp read.
   * Returns a chat reply plus an optional refined price the user can accept.
   */
  async refinePrice(
    payload: {
      itemName: string;
      category?: string;
      condition?: string;
      recommendation: {
        quickSale: number | null;
        balanced: number | null;
        maxValue: number | null;
        low: number | null;
        high: number | null;
        confidence: string;
        marketTrend: string;
        sampleSize: number;
      };
      history: Array<{ role: 'user' | 'assistant'; content: string }>;
      message: string;
    },
    provider: AiProvider = 'groq',
  ): Promise<{
    reply: string;
    suggestedPrice: number | null;
    strategy: PricingStrategyKind | null;
    rationale: string;
  }> {
    const r = await invokeProxy<{
      reply?: unknown;
      suggestedPrice?: unknown;
      strategy?: unknown;
      rationale?: unknown;
    }>(provider, 'refine-price', payload);
    if (!r || typeof r !== 'object' || typeof r.reply !== 'string') {
      throw new Error('AI returned an unexpected response shape for price refinement');
    }
    const strategy =
      r.strategy === 'quick-sale' || r.strategy === 'balanced' || r.strategy === 'max-value'
        ? r.strategy
        : null;
    return {
      reply: r.reply,
      suggestedPrice:
        typeof r.suggestedPrice === 'number' && r.suggestedPrice > 0 ? r.suggestedPrice : null,
      strategy,
      rationale: typeof r.rationale === 'string' ? r.rationale : '',
    };
  },

  /** Fill eBay required item specifics from item context. Returns a map of
   *  aspect name → value; unknown aspects come back as "" (never invented). */
  async fillEbayAspects(
    payload: {
      itemName: string;
      description?: string;
      aspects: Array<{ name: string; mode?: string; allowedValues?: string[] }>;
    },
    provider: AiProvider = 'groq',
  ): Promise<Record<string, string>> {
    const result = await invokeProxy<Record<string, unknown>>(provider, 'fill-aspects', payload);
    if (!result || typeof result !== 'object' || Array.isArray(result)) {
      throw new Error('AI returned an unexpected response shape for aspect fill');
    }
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(result)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  },

  extractKeywords(payload: ExtractKeywordsPayload, provider: AiProvider = 'groq') {
    return invokeProxy<string[]>(provider, 'keywords', payload);
  },

  naturalLanguageQuery(payload: NaturalLanguageQueryPayload, provider: AiProvider = 'groq') {
    return invokeProxy<NaturalLanguageQueryResult>(provider, 'nlq', payload);
  },

  searchComps(payload: SearchCompsPayload, provider: AiProvider = 'groq') {
    return invokeProxy<SearchCompsResult>(provider, 'comps', payload);
  },

  appraise(payload: AppraisePayload, provider: AiProvider = 'groq') {
    return invokeProxy<AppraiseResult>(provider, 'appraise', payload);
  },

  /**
   * Explain a slider-driven valuation in plain English. STRICT-
   * validates the response shape: a malformed / wrong-shaped result throws a
   * clear Error (the Valuation page catches it and hides the block) rather than
   * returning a half-blank explanation card. Never returns a malformed object.
   */
  async explainValuation(
    payload: ExplainValuationPayload,
    provider: AiProvider = 'groq',
  ): Promise<ExplainValuationResult> {
    const r = await invokeProxy<ExplainValuationResult>(provider, 'explain-valuation', payload);
    if (
      !r ||
      typeof r !== 'object' ||
      typeof r.explanation !== 'string' ||
      !Array.isArray(r.drivers) ||
      !['HIGH', 'MEDIUM', 'LOW'].includes(r.confidence_level)
    ) {
      throw new Error('AI returned an unexpected response shape for valuation explanation');
    }
    return r;
  },

  suggestReply(payload: SuggestReplyPayload, provider: AiProvider = 'groq') {
    return invokeProxy<SuggestReplyResult>(provider, 'reply', payload);
  },

  scorePhoto(payload: { imageBase64: string }, provider: AiProvider = 'groq') {
    return invokeProxy<PhotoScoreResult>(provider, 'score-photo', payload);
  },
};
